import React, { useState, useEffect, useCallback } from 'react';
import { 
  getHubSpotIntegrationStatus, 
  initiateHubSpotOAuth, 
  disconnectHubSpot,
  getHubSpotContacts,
  syncHubSpotContacts,
  IntegrationStatus 
} from '../../api/integrations';
import { toast } from 'sonner';
import LoadingComponent from '../LoadingComponent';

interface HubSpotIntegrationProps {
  communityId: string;
  userId: string;
  onClose?: () => void;
}

interface HubSpotContact {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
}

const HubSpotIntegration: React.FC<HubSpotIntegrationProps> = ({ 
  communityId, 
  userId, 
  onClose 
}) => {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [contacts, setContacts] = useState<HubSpotContact[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const integrationStatus = await getHubSpotIntegrationStatus(communityId, userId);
      setStatus(integrationStatus);
    } catch (error) {
      console.error('Error loading HubSpot status:', error);
      toast.error('Failed to load HubSpot integration status');
    } finally {
      setIsLoading(false);
    }
  }, [communityId, userId]);

  const loadContacts = useCallback(async () => {
    try {
      setIsLoadingContacts(true);
      const contactsData = await getHubSpotContacts(communityId, userId);
      setContacts(contactsData.contacts || []);
    } catch (error) {
      console.error('Error loading HubSpot contacts:', error);
      toast.error('Failed to load HubSpot contacts');
    } finally {
      setIsLoadingContacts(false);
    }
  }, [communityId, userId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.connected) {
      loadContacts();
    }
  }, [status?.connected, loadContacts]);

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      const response = await initiateHubSpotOAuth(communityId, userId);
      if (response.authUrl) {
        // Open OAuth flow in new window
        const popup = window.open(
          response.authUrl,
          'HubSpot OAuth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        // Listen for OAuth completion
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            setIsConnecting(false);
            loadStatus(); // Refresh status
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error connecting HubSpot:', error);
      toast.error('Failed to initiate HubSpot connection');
      setIsConnecting(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      await syncHubSpotContacts(communityId, userId);
      toast.success('HubSpot contacts synced successfully');
      await loadContacts();
    } catch (error) {
      console.error('Error syncing HubSpot contacts:', error);
      toast.error('Failed to sync HubSpot contacts');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect HubSpot?')) {
      return;
    }

    try {
      await disconnectHubSpot(communityId, userId);
      toast.success('HubSpot disconnected successfully');
      await loadStatus();
      setContacts([]);
    } catch (error) {
      console.error('Error disconnecting HubSpot:', error);
      toast.error('Failed to disconnect HubSpot');
    }
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <div className="w-full p-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">HubSpot Integration</h2>
          {status?.connected && (
            <div className="flex gap-2">
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="px-4 py-2 text-sm font-medium text-primary-200 hover:text-primary-300 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50"
              >
                {isSyncing ? 'Syncing...' : 'Sync Contacts'}
              </button>
              <button
                onClick={handleDisconnect}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50"
              >
                Disconnect
              </button>
            </div>
          )}
        </div>

        {status?.connected ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-green-800 font-medium">Connected</span>
              </div>
            </div>

            {status.accountName && (
              <div className="text-sm text-gray-600">
                <p><strong>Account:</strong> {status.accountName}</p>
                {status.lastSyncedAt && (
                  <p><strong>Last Synced:</strong> {new Date(status.lastSyncedAt).toLocaleString()}</p>
                )}
              </div>
            )}

            {/* Contacts List */}
            {isLoadingContacts ? (
              <LoadingComponent />
            ) : (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Contacts</h3>
                {contacts.length > 0 ? (
                  <ul className="space-y-2 max-h-64 overflow-y-auto">
                    {contacts.slice(0, 10).map((contact) => (
                      <li key={contact.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {contact.firstName && contact.lastName 
                              ? `${contact.firstName} ${contact.lastName}`
                              : contact.email}
                          </span>
                          {contact.company && (
                            <p className="text-xs text-gray-500">{contact.company}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{contact.email}</span>
                      </li>
                    ))}
                    {contacts.length > 10 && (
                      <li className="text-sm text-gray-500 text-center py-2">
                        And {contacts.length - 10} more contacts...
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-center py-4">No contacts found</p>
                )}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Available Features</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Sync contacts and deals</li>
                <li>• View and manage HubSpot contacts</li>
                <li>• Access deal pipeline information</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Connect your HubSpot account to sync contacts and deals directly from your dashboard.
            </p>
            <button
              onClick={handleConnect}
              disabled={isConnecting}
              className="px-6 py-3 bg-primary-200 hover:bg-primary-300 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? 'Connecting...' : 'Connect HubSpot'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HubSpotIntegration;

